import React from 'react';
import { Check } from 'lucide-react';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { cn } from '../../lib/utils';

/**
 * Selector de tarjetas para elegir servicio, profesional o recurso.
 *
 * Antes cada tarjeta era un <div> con onClick, sin role ni tabIndex, y adentro
 * tenía un <button> con stopPropagation que hacía exactamente lo mismo: dos
 * objetivos táctiles superpuestos para la misma acción, y nada de esto era
 * navegable con teclado ni anunciable por un lector de pantalla.
 *
 * Ahora es un RadioGroup de Radix, que es lo que esto siempre fue
 * conceptualmente: elegir una opción de varias. Trae gratis la navegación con
 * flechas, el foco visible y la semántica correcta.
 *
 * El color del estado seleccionado sale del token --primary, que lib/branding.js
 * completa con el color del negocio.
 */

// Tailwind necesita las clases completas en el código para poder generarlas,
// así que no se puede armar `lg:grid-cols-${n}` con una plantilla.
const COLUMNAS = {
  1: 'sm:grid-cols-1 lg:grid-cols-1',
  2: 'sm:grid-cols-2 lg:grid-cols-2',
  3: 'sm:grid-cols-2 lg:grid-cols-3',
};

export default function SelectorOpciones({
  opciones,
  valor,
  onSeleccionar,
  renderContenido,
  maxColumnas = 3,
  ariaLabel,
}) {
  // Con pocas opciones se limita la cantidad de columnas para que las tarjetas
  // no queden angostas y solas con media pantalla vacía al lado.
  const columnas = COLUMNAS[Math.min(opciones.length, maxColumnas)] || COLUMNAS[3];

  return (
    <RadioGroup
      value={valor ?? ''}
      onValueChange={(v) => {
        const elegida = opciones.find((o) => o.valor === v);
        if (elegida) onSeleccionar(elegida.original);
      }}
      aria-label={ariaLabel}
      className={cn('grid grid-cols-1 gap-3 sm:gap-4', columnas)}
    >
      {opciones.map((opcion) => {
        const seleccionada = valor === opcion.valor;
        return (
          <label key={opcion.valor} className="cursor-pointer">
            <RadioGroupItem value={opcion.valor} className="peer sr-only" />
            <div
              className={cn(
                'relative flex h-full flex-col rounded-2xl border bg-white p-5 transition-colors',
                'peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2',
                seleccionada
                  ? 'border-primary ring-1 ring-primary'
                  : 'border-neutral-200 hover:border-neutral-300 hover:bg-neutral-50'
              )}
            >
              {seleccionada && (
                <span
                  className="absolute right-4 top-4 inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground"
                  aria-hidden="true"
                >
                  <Check className="h-3.5 w-3.5" />
                </span>
              )}
              {renderContenido(opcion.original, seleccionada)}
            </div>
          </label>
        );
      })}
    </RadioGroup>
  );
}
